import { useEffect } from 'react';
import { useAuth, useScopes } from '@discovr/common';
import { identifyClarityUser, initClarity, setClarityTags } from './clarity';
import { useClarityConsent } from './clarityConsent';

/**
 * Starts Clarity once a signed-in user has accepted analytics cookies, then links the session to the
 * Firebase uid (hashed by Clarity) and tags it with role + brandId so recordings
 * are filterable by segment. Mount once, inside the providers (AuthProvider +
 * QueryClientProvider), and pass the build-time project id; the wrapper no-ops
 * without one, so Clarity stays production-only.
 *
 * Clarity loads here rather than at app boot so signed-out pages are never
 * recorded: login, password reset, invite links (whose URLs carry tokens and
 * emails) and anonymous public-share visitors (issue #727).
 *
 * Tags are best-effort: scopes load asynchronously after auth, so the effect
 * re-runs once role/brandId resolve and the tags are applied then. Absent values
 * (admins have no brandId; a still-loading or failed scopes query) are simply
 * skipped — the recording and identify still apply.
 */
export function useClarityIdentity(projectId: string | undefined): void {
  const { user } = useAuth();
  const { role, brandId } = useScopes();
  const consent = useClarityConsent();
  const uid = user?.uid;

  useEffect(() => {
    if (!uid || consent !== true) return;
    initClarity(projectId);
    identifyClarityUser({ uid });
    setClarityTags({ role, brandId });
  }, [projectId, uid, role, brandId, consent]);
}
